import Button from "../models/Button";
import ButtonChannel from "../models/ButtonChannel";
import Consumer from "../models/Consumer";
import Floor from "../models/Floor";
import Multiplexer from "../models/Multiplexer";
import type Settings from "../models/Settings";
import Signal from "../models/Signal";
import { descriptionForSignalType, shortDescriptionForSignalType } from "../models/SignalTypes";

export default class GroupAddressNameGenerator {
  settings: Settings;

  get buttonChannelTemplate(): string {
    return this.settings.groupAddresses.buttonChannelNameTemplate || this.settings.default_buttonChannelNameTemplate;
  }

  get consumerTemplate(): string {
    return this.settings.groupAddresses.consumerNameTemplate || this.settings.default_consumerNameTemplate;
  }

  get multiplexerTemplate(): string {
    return this.settings.groupAddresses.multiplexerNameTemplate || this.settings.default_multiplexerNameTemplate;
  }

  private buttonChannelFunction: Function | undefined;
  private consumerFunction: Function | undefined;
  private multiplexerFunction: Function | undefined;

  setTemplates() {
    const paramsButton = [
      "floor", // no itx
      "room", // no itx
      "button", // no itx
      "buttonChannel", // no itx
      "signal", // no itx
      "signalType", // no itx
      "signalTypeShort", // no itx
    ];

    const paramsConsumer = [
      "floor", // no itx
      "room", // no itx
      "consumer", // no itx
      "signal", // no itx
      "signalType", // no itx
      "signalTypeShort", // no itx
    ];

    const paramsMultiplexer = [
      "multiplexer", // no itx
      "signal", // no itx
      "signalType", // no itx
      "signalTypeShort", // no itx
    ];

    this.buttonChannelFunction = new Function(...paramsButton, "return `" + this.buttonChannelTemplate + "`");

    this.consumerFunction = new Function(...paramsConsumer, "return `" + this.consumerTemplate + "`");

    this.multiplexerFunction = new Function(...paramsMultiplexer, "return `" + this.multiplexerTemplate + "`");
  }

  generateGroupAddressNameForMultiplexer(multiplexer: Multiplexer, signal: Signal): string {
    try {
      const signalType = descriptionForSignalType(signal.type);
      const signalTypeShort = shortDescriptionForSignalType(signal.type);

      const name =
        (this.multiplexerFunction && this.multiplexerFunction(multiplexer, signal, signalType, signalTypeShort)) || "";

      return name;
    } catch (ex: any) {
      return "Error: " + ex.message;
    }
  }

  generateGroupAddressNameForButtonChannel(buttonChannel: ButtonChannel, signal: Signal): string {
    try {
      const signalType = descriptionForSignalType(signal.type);
      const signalTypeShort = shortDescriptionForSignalType(signal.type);
      const button = buttonChannel.parent as Button;
      const room = button.room;
      const floor = room?.parent as Floor;

      const name =
        (this.buttonChannelFunction &&
          this.buttonChannelFunction(floor, room, button, buttonChannel, signal, signalType, signalTypeShort)) ||
        "";
      // name can be max 32 characters, so we need to truncate it if it's too long
      if (name.length > 32) {
        return name.substring(0, 29) + "...";
      }

      return name;
    } catch (ex: any) {
      return "Error: " + ex.message;
    }
  }

  generateGroupAddressNameForConsumer(consumer: Consumer, signal: Signal): string {
    try {
      const signalType = descriptionForSignalType(signal.type);
      const signalTypeShort = shortDescriptionForSignalType(signal.type);
      const room = consumer.room;
      const floor = room?.parent as Floor;

      const name =
        (this.consumerFunction && this.consumerFunction(floor, room, consumer, signal, signalType, signalTypeShort)) ||
        "";

      return name;
    } catch (ex: any) {
      return "Error: " + ex.message;
    }
  }

  constructor(settings: Settings) {
    this.settings = settings;
  }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////
